// Groups transcript segments into practice sentences: manual tracks split on
// punctuation, auto (unpunctuated) tracks on pauses, both with duration caps.

import { SENTENCE_END_RE as TERMINAL_PUNCTUATION } from './sentenceLang';
import { segmentsToWords, buildSentences as buildSentencesFromWords } from './sentenceBuilder';
const MANUAL_MAX_DURATION = 12;
const ASR_MAX_DURATION = 7;
const ASR_GAP_BOUNDARY = 0.7;

// Split cues at internal sentence ends (cues follow screen layout, not grammar),
// timing interpolated by word count.
export function splitSegmentsAtSentenceEnds(segments) {
  const out = [];

  (segments || []).forEach(seg => {
    const words = String(seg.text || '').trim().split(/\s+/).filter(Boolean);

    if (words.length < 2) {
      out.push({ ...seg });
      return;
    }

    const groups = [];
    let group = [];
    words.forEach(word => {
      group.push(word);
      if (TERMINAL_PUNCTUATION.test(word)) {
        groups.push(group);
        group = [];
      }
    });
    if (group.length > 0) groups.push(group);

    if (groups.length < 2) {
      out.push({ ...seg });
      return;
    }

    const duration = seg.dur || 0;
    let consumed = 0;
    groups.forEach(g => {
      out.push({
        text: g.join(' '),
        start: seg.start + duration * (consumed / words.length),
        dur: duration * (g.length / words.length)
      });
      consumed += g.length;
    });
  });

  return out;
}

export function buildSentences(rawSegments, isGenerated) {
  const segments = isGenerated ? rawSegments : splitSegmentsAtSentenceEnds(rawSegments);
  const sentences = [];
  let current = null;

  const flush = () => {
    if (current && current.text) {
      sentences.push(current);
    }
    current = null;
  };

  (segments || []).forEach(seg => {
    const text = (seg.text || '')
      // Same annotation classes scoring.js strips, so no empty scoring reference.
      .replace(/\[[^\]]*\]|\([^)]*\)/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const segEnd = seg.start + (seg.dur || 0);

    if (!text) {
      flush();
      return;
    }

    if (current) {
      const gap = seg.start - current.end;
      const mergedDuration = segEnd - current.start;
      const overLimit = isGenerated
        ? gap > ASR_GAP_BOUNDARY || mergedDuration > ASR_MAX_DURATION
        : mergedDuration > MANUAL_MAX_DURATION;
      if (overLimit) flush();
    }

    if (!current) {
      current = { text: '', start: seg.start, end: segEnd };
    }
    current.text = (current.text ? current.text + ' ' : '') + text;
    current.end = Math.max(current.end, segEnd);

    if (!isGenerated && TERMINAL_PUNCTUATION.test(text)) {
      flush();
    }
  });

  flush();
  return sentences;
}

// First sentence still (mostly) ahead of `time`, else the last.
export function findSentenceIndex(sentences, time, tolerance = 0.3) {
  if (!sentences || sentences.length === 0) return -1;
  for (let i = 0; i < sentences.length; i++) {
    if (sentences[i].end > time + tolerance) return i;
  }
  return sentences.length - 1;
}

// Auto-generated tracks carry word timestamps (<s>) for exact boundaries;
// manual tracks fall back to the cue-level splitter.
export function sentencesFromTranscript({ segments, isGenerated }) {
  const hasWordData = isGenerated && segments.length > 0 && segments[0].words;
  const raw = hasWordData
    ? buildSentencesFromWords(segmentsToWords(segments))
    : buildSentences(segments, isGenerated);
  // The word-level path doesn't strip annotation brackets ([Music], (applause)),
  // so filter sentences that are empty or annotation-only after stripping.
  return raw.filter(s => s.text && s.text.replace(/\[[^\]]*\]|\([^)]*\)/g, '').trim());
}
